import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Typography from '@material-ui/core/Typography';
import TextField from 'components/fields/TextField';
import CheckboxField from 'components/fields/CheckboxField';
import FormError from 'components/common/FormError';
import IconButton from 'components/common/IconButton';
import IconCheckboxSelect from 'components/icons/IconCheckboxSelect';
import IconCheckboxChecked from 'components/icons/IconCheckboxChecked';
import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import Link from 'components/common/Link';
import IconTrashCan from 'components/icons/IconTrashCan';

import {
  createReportStructure,
  updateReportStructure,
} from 'redux/modules/reportStructures';

import * as validate from 'utils/validate';
import objectDiff from 'utils/objectDiff';
import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

const styles = theme => ({
  typographySubtitle: {
    marginTop: 24,
  },
  typographyLabel: {
    marginBottom: 6,
    marginTop: 24,
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
  },
  error: {
    marginBottom: 24,
  },

  iconCheckboxSelect: {
    width: 18,
    height: 18,
    color: theme.palette.primary.A400,
  },
  iconCheckboxChecked: {
    width: 18,
    height: 18,
    color: theme.palette.primary[500],
  },
  iconButton: {
    margin: '-5px 0 -5px -14px',
  },
  sectionContent: {
    paddingLeft: 32,
  },
  section: {
    marginTop: 16,
    maxWidth: '20%',
    flexBasis: '20%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  iconTrashCan: {
    width: 16,
    height: 16,
    margin: '0 6px 0 0',
    verticalAlign: 'top',
  },
  linkButton: {
    marginBottom: 8,
  },
});

function onSubmit(values, dispatch, props) {
  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateReportStructure({
        values: {
          ID: props.ID,
          ...diffValues.Structure ? { 'Report Type': 'Order' } : {},
          ...diffValues,
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.description });
        }
      throw new SubmissionError(err);
      });
  }
  return new Promise((resolve, reject) => {
    dispatch(createReportStructure({
      values,
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.description });
      }
      throw new SubmissionError(err);
    });
}

const validateForm = (values, props) => {
  const errors = {};
  if (Object.keys(values.Structure).filter(key => values.Structure[key] === true).length === 0) {
    errors._error = 'Please, select at least one column';
  }
  return errors;
}

class ReportStructureCreateEditForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteButtonClick = this.onDeleteButtonClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onDeleteButtonClick(event) {
    event.stopPropagation();
    this.setState({ anchorEl: event.currentTarget });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { actions, onDelete } = this.props;
    this.setState({ anchorEl: null });
    onDelete();
  }

  render() {
    const {
      className,
      handleSubmit,
      defaultReportStructure,
      classes,
      initialValues,
      formValues,
      error,
      change,
      account,
    } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    const groupedDefaultReportStructure = defaultReportStructure.reduce((accumulator, currentValue) => {
      if (['Patient', 'DOB', 'Prescriber', 'Referral Source',
        'Primary Insurance', 'Insurance', 'Diagnosis', 'Sales Person'].indexOf(currentValue) !== -1) {
        return {
          ...accumulator,
          'Demographic': [
            ...accumulator['Demographic'] || [],
            currentValue,
          ],
        };
      }
      if (['Create At', 'Assigned From', 'Assigned To', 'Workflow Status'].indexOf(currentValue) !== -1) {
        return {
          ...accumulator,
          'Workflow Details': [
            ...accumulator['Workflow Details'] || [],
            currentValue,
          ],
        };
      }
      if (['Date of Service From', 'Date of Service To', 'HCPC S Code',
        'Type', 'Refill', 'Date of Delivery'].indexOf(currentValue) !== -1) {
        return {
          ...accumulator,
          'Order Details': [
            ...accumulator['Order Details'] || [],
            currentValue,
          ],
        };
      }
      if (['Transmission Method', 'Medic Type', 'Modifiers', 'Units',
        'Cost', 'Charge', 'Place of Service'].indexOf(currentValue) !== -1) {
        return {
          ...accumulator,
          'Claim Details': [
            ...accumulator['Claim Details'] || [],
            currentValue,
          ],
        };
      }
      if (['Is Rejected', 'Reimbs Insurance', 'Reimbs', 'Reimbs Date',
        'Check Insurance', 'Check Number'].indexOf(currentValue) !== -1) {
        return {
          ...accumulator,
          'Collection Details': [
            ...accumulator['Collection Details'] || [],
            currentValue,
          ],
        };
      }
      return {
        ...accumulator,
        'Other': [
          ...accumulator['Other'] || [],
          currentValue,
        ],
      };
    }, {});
    const allFieldsSelected = fields =>
      formValues ? groupedDefaultReportStructure[fields].length === groupedDefaultReportStructure[fields]
        .filter(item => formValues.Structure[item]).length : false;
    const onSelectDeselectAllGroupClick = (fields) => {
      if (allFieldsSelected(fields)) {
        groupedDefaultReportStructure[fields].forEach((item) => {
          change( `Structure.${item}`, false);
        });
      } else {
        groupedDefaultReportStructure[fields].forEach((item) => {
          change( `Structure.${item}`, true);
        });
      }
    };
    return (
      <form
        className={className}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >    
        {error &&
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <FormError className={classes.error}>
                {error}
              </FormError>
            </Grid>
          </Grid>
        }
        <Grid
          container
          spacing={24}
          alignItems="flex-end"
        >
          <Grid item xs={12} md={4}>
            <Field
              id="Default Report Structure Name"
              name="Name"
              label="Template Name"
              component={TextField}
              validate={validate.required}
              fullWidth
            />
          </Grid>
          {(initialValues.ID && account.Roles.filter(item =>
            item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length > 0) &&
            <Grid item>
              <Link
                className={classes.linkButton}
                color="secondary"
                bold
                aria-label="Remove"
                aria-owns={open ? `${initialValues.ID}-report-remove-menu` : null}
                aria-haspopup="true"
                onClick={this.onDeleteButtonClick}
              >
                <IconTrashCan className={classes.iconTrashCan} />
                Delete
              </Link>
              <Menu
                id={`${initialValues.ID}-report-remove-menu`}
                anchorEl={anchorEl}
                open={open}
                onClose={this.onMenuClose}
                list={false}
              >
                <DeleteMenu
                  text="Are you sure you want to delete this report columns structure template?"
                  onCancel={this.onDeleteCancel}
                  onSubmit={this.onDeleteSubmit}
                />
              </Menu>
            </Grid>
          }
        </Grid>
        <Grid
          container
          spacing={0}
          alignItems="center"
        >
          <Grid item xs={12}>
            <Typography
              className={classes.typographySubtitle}
              color="secondary"
            >
              Select the columns you want to include in your report:
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={8}
          alignItems="flex-start"
        >
          {Object.keys(groupedDefaultReportStructure).map(groupKey => (
            <Grid item key={groupKey} xs={12} md={3} className={classes.section}>
              <Grid
                container
                spacing={0}
                alignItems="center"
              >
                <Grid item>
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => onSelectDeselectAllGroupClick(groupKey)}
                    tooltipContent={allFieldsSelected(groupKey) ? 'Deselect All' : 'Select All'}
                  >
                    {allFieldsSelected(groupKey) ? (
                      <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                    ) : (
                      <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                    )}
                  </IconButton>
                </Grid>
                <Grid item>
                  <Typography color="secondary">
                    {groupKey}
                  </Typography>
                </Grid>
              </Grid>
              <div className={classes.sectionContent}>
                <Grid
                  container
                  spacing={0}
                  alignItems="flex-start"
                  direction="column"
                >
                  {groupedDefaultReportStructure[groupKey].map(item => (
                    <Grid
                      xs={12}
                      item
                      key={item}
                      className={classes.gridItem}
                    >
                      <Field
                        id={item}
                        name={`Structure.${item}`}
                        label={item}
                        component={CheckboxField}
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </Grid>
          ))}
        </Grid>
      </form>
    );
  }
}

ReportStructureCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  error: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    section: PropTypes.string.isRequired,
  }).isRequired,
};

ReportStructureCreateEditForm.defaultProps = {
  className: '',
  error: null,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
})(withStyles(styles)(ReportStructureCreateEditForm));



// WEBPACK FOOTER //
// ./src/components/reports/ReportStructureCreateEditDialog/ReportStructureCreateEditForm.js